import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  getAdminPhases,
  createPhase,
  updatePhase,
  deletePhase,
  openPhase,
  closePhase,
  type AdminSaleEventPhase,
} from '../lib/api'
import Modal from '../components/Modal'
import { useConfirm } from '../components/ConfirmDialog'
import RowActions from '../components/RowActions'

interface PhaseFormState {
  name: string
  price: string
  stock: string
}

const blankPhaseForm: PhaseFormState = { name: '', price: '', stock: '' }

function phaseStatus(p: AdminSaleEventPhase): 'open' | 'sold_out' | 'closed' {
  if (p.is_open) return 'open'
  if ((p.stock_sold ?? 0) >= p.stock && p.stock > 0) return 'sold_out'
  return 'closed'
}

export default function SaleEventPhasesPage() {
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  const { confirm, dialog: confirmDialog } = useConfirm()

  const [phases, setPhases] = useState<AdminSaleEventPhase[]>([])
  const [loading, setLoading] = useState(true)
  const [phaseError, setPhaseError] = useState<string | null>(null)

  const [phaseModal, setPhaseModal] = useState<{ open: boolean; mode: 'add' | 'edit'; phase?: AdminSaleEventPhase }>({ open: false, mode: 'add' })
  const [modalForm, setModalForm] = useState<PhaseFormState>(blankPhaseForm)
  const [modalSaving, setModalSaving] = useState(false)

  const input = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'
  const labelCls = 'block text-xs text-zinc-400 mb-1.5'

  async function reloadPhases() {
    if (!id || !token) return
    const p = await getAdminPhases(token, id)
    setPhases(p)
  }

  useEffect(() => {
    if (!id || !token) return
    setLoading(true)
    getAdminPhases(token, id)
      .then(setPhases)
      .finally(() => setLoading(false))
  }, [id, token])

  function openAddModal() {
    setModalForm(blankPhaseForm)
    setPhaseError(null)
    setPhaseModal({ open: true, mode: 'add' })
  }

  function openEditModal(phase: AdminSaleEventPhase) {
    setModalForm({ name: phase.name, price: String(phase.price), stock: String(phase.stock) })
    setPhaseError(null)
    setPhaseModal({ open: true, mode: 'edit', phase })
  }

  function closeModal() {
    setPhaseModal({ open: false, mode: 'add' })
    setModalForm(blankPhaseForm)
    setPhaseError(null)
  }

  async function handleModalSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token || !id) return
    setModalSaving(true)
    setPhaseError(null)
    try {
      if (phaseModal.mode === 'add') {
        await createPhase(token, id, {
          name: modalForm.name,
          price: parseInt(modalForm.price, 10),
          stock: parseInt(modalForm.stock, 10),
          display_order: phases.length,
        })
      } else if (phaseModal.phase) {
        await updatePhase(token, id, phaseModal.phase.id, {
          name: modalForm.name,
          price: parseInt(modalForm.price, 10),
          stock: parseInt(modalForm.stock, 10),
        })
      }
      closeModal()
      await reloadPhases()
    } catch (err) {
      setPhaseError(err instanceof Error ? err.message : 'Gagal menyimpan phase')
    } finally {
      setModalSaving(false)
    }
  }

  async function handleDeletePhase(phaseId: string, phaseName: string) {
    const ok = await confirm(`Hapus phase "${phaseName}"? Tindakan ini tidak dapat dibatalkan.`, { confirmLabel: 'Hapus Phase' })
    if (!ok || !token || !id) return
    setPhaseError(null)
    try {
      await deletePhase(token, id, phaseId)
      await reloadPhases()
    } catch (err) {
      setPhaseError(err instanceof Error ? err.message : 'Gagal menghapus phase')
    }
  }

  async function handleOpenPhase(phaseId: string) {
    if (!token || !id) return
    setPhaseError(null)
    try {
      await openPhase(token, id, phaseId)
      setPhases(prev => prev.map(p => ({ ...p, is_open: p.id === phaseId })))
    } catch (err) {
      setPhaseError(err instanceof Error ? err.message : 'Gagal membuka phase')
    }
  }

  async function handleClosePhase(phaseId: string) {
    if (!token || !id) return
    setPhaseError(null)
    try {
      await closePhase(token, id, phaseId)
      setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, is_open: false } : p))
    } catch (err) {
      setPhaseError(err instanceof Error ? err.message : 'Gagal menutup phase')
    }
  }

  async function handleMovePhase(index: number, direction: 'up' | 'down') {
    if (!token || !id) return
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= phases.length) return
    const a = phases[index]
    const b = phases[targetIndex]
    if (!a || !b) return
    setPhaseError(null)
    try {
      await Promise.all([
        updatePhase(token, id, a.id, { display_order: b.display_order }),
        updatePhase(token, id, b.id, { display_order: a.display_order }),
      ])
      await reloadPhases()
    } catch (err) {
      setPhaseError(err instanceof Error ? err.message : 'Gagal mengubah urutan phase')
    }
  }

  return (
    <div className="p-6">
      {confirmDialog}

      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
              Phases ({loading ? '…' : phases.length})
            </h2>
          </div>
          <button
            onClick={openAddModal}
            className="text-xs font-semibold text-yellow-500 hover:text-yellow-400 transition-colors"
          >
            + Tambah Phase
          </button>
        </div>

        <div className="p-5">
          {phaseError && (
            <p className="mb-4 rounded border border-red-800 bg-red-900/20 px-3 py-2 text-xs text-red-400">{phaseError}</p>
          )}

          {loading ? (
            <p className="text-zinc-500 text-sm">Loading…</p>
          ) : phases.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-zinc-500 text-sm">Belum ada phase.</p>
              <p className="text-zinc-600 text-xs mt-1">Tambah phase untuk mulai menjual tiket.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {phases.map((p, idx) => {
                const status = phaseStatus(p)
                return (
                  <div key={p.id} className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
                    <div className="flex items-center gap-3">
                      {/* Order controls */}
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          onClick={() => handleMovePhase(idx, 'up')}
                          disabled={idx === 0}
                          className="text-zinc-600 hover:text-zinc-300 disabled:opacity-20 disabled:cursor-not-allowed text-[10px] leading-none"
                          title="Naikan urutan"
                        >▲</button>
                        <button
                          onClick={() => handleMovePhase(idx, 'down')}
                          disabled={idx === phases.length - 1}
                          className="text-zinc-600 hover:text-zinc-300 disabled:opacity-20 disabled:cursor-not-allowed text-[10px] leading-none"
                          title="Turunkan urutan"
                        >▼</button>
                      </div>

                      {/* Name */}
                      <span className="flex-1 text-sm font-medium text-white">{p.name}</span>

                      {/* Status badge */}
                      {status === 'open' && <span className="text-[10px] font-bold text-green-400 shrink-0">● OPEN</span>}
                      {status === 'sold_out' && <span className="text-[10px] font-bold text-red-400 shrink-0">SOLD OUT</span>}
                      {status === 'closed' && <span className="text-[10px] text-zinc-600 shrink-0">CLOSED</span>}

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {status === 'open' ? (
                          <button
                            onClick={() => handleClosePhase(p.id)}
                            className="rounded px-2 py-0.5 text-[10px] border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
                          >Close</button>
                        ) : status === 'closed' ? (
                          <button
                            onClick={() => handleOpenPhase(p.id)}
                            className="rounded px-2 py-0.5 text-[10px] border border-green-800 text-green-400 hover:border-green-600 transition-colors"
                          >Open</button>
                        ) : null}
                        <RowActions
                          actions={[
                            { label: 'Edit Phase', onClick: () => openEditModal(p) },
                            { label: 'Hapus Phase', onClick: () => handleDeletePhase(p.id, p.name), variant: 'danger' },
                          ]}
                        />
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="mt-2 ml-6 flex gap-3 text-[11px] text-zinc-500">
                      <span>Rp {p.price.toLocaleString('id-ID')}</span>
                      <span>·</span>
                      <span>Stok {p.stock}</span>
                      <span>·</span>
                      <span>Terjual {p.stock_sold ?? 0}</span>
                      <span>·</span>
                      <span>Sisa {p.stock_remaining ?? p.stock}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Phase Modal */}
      {phaseModal.open && (
        <Modal
          title={phaseModal.mode === 'add' ? 'Tambah Phase' : 'Edit Phase'}
          onClose={closeModal}
        >
          <form onSubmit={handleModalSubmit} className="space-y-4">
            {phaseError && (
              <p className="rounded border border-red-800 bg-red-900/20 px-3 py-2 text-xs text-red-400">{phaseError}</p>
            )}
            <div>
              <label className={labelCls}>Nama Phase <span className="text-red-400">*</span></label>
              <input
                type="text"
                className={input}
                value={modalForm.name}
                onChange={e => setModalForm(f => ({ ...f, name: e.target.value }))}
                placeholder="contoh: Early Bird, Regular, VIP"
                required
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Harga <span className="text-red-400">*</span></label>
                <input
                  type="number"
                  className={input}
                  value={modalForm.price}
                  onChange={e => setModalForm(f => ({ ...f, price: e.target.value }))}
                  placeholder="150000"
                  min={0}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Stok <span className="text-red-400">*</span></label>
                <input
                  type="number"
                  className={input}
                  value={modalForm.stock}
                  onChange={e => setModalForm(f => ({ ...f, stock: e.target.value }))}
                  placeholder="100"
                  min={0}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={modalSaving}
                className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
              >
                {modalSaving ? 'Menyimpan…' : phaseModal.mode === 'add' ? 'Tambah Phase' : 'Simpan Perubahan'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
